import { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, User, Loader2 } from 'lucide-react';

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
}

interface AIChatbotProps {
  onClose: () => void;
}

const quickReplies = [
  'Track my order',
  'View products',
  'Check wallet balance',
  'Contact support',
  'Delivery status',
];

const AIChatbot: React.FC<AIChatbotProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! 👋 I'm your GrocerySub assistant. How can I help you today?",
      isBot: true,
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getBotResponse = (userMessage: string): string => {
    const msg = userMessage.toLowerCase();

    if (msg.includes('track') || msg.includes('order status')) {
      return "To track your order, go to the Orders page from the sidebar. You'll see all your orders with their current status. Would you like me to help with anything else?";
    }
    if (msg.includes('product') || msg.includes('shop')) {
      return "You can browse all our products on the Products page. We have fresh fruits, vegetables, dairy, bakery items and more! Use the category filters to find what you need.";
    }
    if (msg.includes('wallet') || msg.includes('balance') || msg.includes('payment')) {
      return "Your wallet balance and transaction history can be found on the Wallet page. You can add money using various payment methods. Current offers: Get 5% cashback on wallet recharge above ₹500!";
    }
    if (msg.includes('delivery') || msg.includes('shipping')) {
      return "We deliver 7 days a week from 7 AM to 9 PM. Standard delivery is free for orders above ₹500. Express delivery (within 2 hours) is available for ₹49 extra.";
    }
    if (msg.includes('subscription')) {
      return "With subscriptions, you can schedule regular deliveries of your favorite products. Go to Subscriptions page to set up daily, weekly, or monthly deliveries and save up to 10%!";
    }
    if (msg.includes('support') || msg.includes('help') || msg.includes('contact')) {
      return "For support, you can:\n• Email: support@acentle.com\n• Phone: 1800-123-4567 (9 AM - 9 PM)\n• Or describe your issue here and I'll try to help!";
    }
    if (msg.includes('refund') || msg.includes('return')) {
      return "We have a hassle-free return policy. If you're not satisfied with any product, you can request a refund within 24 hours of delivery. Go to Orders → Select Order → Request Refund.";
    }
    if (msg.includes('offer') || msg.includes('discount') || msg.includes('coupon')) {
      return "Current offers:\n🎉 FRESH10 - 10% off on first order\n🥬 VEGGIES20 - 20% off on vegetables\n🍎 FRUITS15 - 15% off on fruits\nApply at checkout!";
    }
    if (msg.includes('hi') || msg.includes('hello') || msg.includes('hey')) {
      return "Hello! 👋 How can I assist you today? You can ask me about orders, products, delivery, wallet, or anything else!";
    }
    if (msg.includes('thank')) {
      return "You're welcome! 😊 Is there anything else I can help you with?";
    }

    return "I'm here to help! You can ask me about:\n• Order tracking\n• Products & categories\n• Wallet & payments\n• Delivery information\n• Subscriptions\n• Offers & discounts\n\nWhat would you like to know?";
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: input,
      isBot: false,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate bot typing
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getBotResponse(input),
        isBot: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000);
  };

  const handleQuickReply = (reply: string) => {
    setInput(reply);
    setTimeout(() => handleSend(), 100);
  };

  return (
    <div className="fixed bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot size={24} />
          </div>
          <div>
            <h3 className="font-semibold">GrocerySub Assistant</h3>
            <p className="text-xs text-white/80">Always here to help</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-white/20 rounded-full transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-80 min-h-[200px]">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex items-start gap-2 ${msg.isBot ? '' : 'flex-row-reverse'}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.isBot ? 'bg-[#10b981] text-white' : 'bg-gray-200 text-gray-600'
                }`}
            >
              {msg.isBot ? <Bot size={16} /> : <User size={16} />}
            </div>
            <div
              className={`max-w-[75%] p-3 rounded-2xl ${msg.isBot
                ? 'bg-gray-100 text-gray-800 rounded-tl-none'
                : 'bg-[#10b981] text-white rounded-tr-none'
                }`}
            >
              <p className="text-sm whitespace-pre-line">{msg.text}</p>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#10b981] rounded-full flex items-center justify-center text-white">
              <Bot size={16} />
            </div>
            <div className="bg-gray-100 p-3 rounded-2xl rounded-tl-none">
              <Loader2 size={16} className="animate-spin text-gray-500" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Replies */}
      <div className="px-4 pb-2">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {quickReplies.map((reply) => (
            <button
              key={reply}
              onClick={() => handleQuickReply(reply)}
              className="flex-shrink-0 px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded-full hover:bg-gray-200 transition-colors"
            >
              {reply}
            </button>
          ))}
        </div>
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#10b981] focus:border-transparent"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="w-10 h-10 bg-[#10b981] text-white rounded-full flex items-center justify-center hover:bg-[#059669] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIChatbot;